import * as React from "react";
import {observer} from "mobx-react";
import {withAppStores} from "../../../AppStores";
import type {TransportLayer} from "../../../data/TransportLayer";
import type {Navigation} from "../../../Navigation";
import type {AppState} from "../../../data/state/AppState";
import {SVGIcon} from "../../widgets/button/SVGIcon";
import {FormContainerStyled, LoginWindowStyled} from "../user/UserStaticV5.Style";
import {LoaderIconV5} from "../loader/LoaderIconV5";
import CircleQuestionIcon from "../icons/circle-question.svg?react";
import {IconButton} from "../../widgets/button/IconButton";
import {handleLogin} from "../../../utils/LoginUtils";
import {ReactUtils} from "../../utils/ReactUtils";
import type {XyiconLoginAndSSOLoginState} from "../../windows/login/LoginWindow";

interface IILoginWindowProps {
	readonly navigation?: Navigation;
	readonly transport?: TransportLayer;
	readonly appState?: AppState;
	readonly isOrganizationSwitchView?: boolean;
	readonly handleClose?: () => void;
	readonly handleOrganizationSwitch?: () => void;
}
@withAppStores("navigation", "transport", "appState")
@observer
export class LoginWindowV5 extends React.Component<IILoginWindowProps, XyiconLoginAndSSOLoginState> {
	constructor(props: IILoginWindowProps) {
		super(props);

		this.state = {
			message: "",
			inputString: "",
		};
	}

	public override componentDidMount() {
		window.addEventListener("hashchange", this.onHashChange);
	}

	public override componentWillUnmount() {
		window.removeEventListener("hashchange", this.onHashChange);
	}

	private onHashChange = () => {
		this.props.appState.isLoggingIn = false;
	};

	private onForgottenPasswordClick = () => {
		this.props.navigation.go("auth/forgotten_password");
	};

	private goToLoginWithSSO = () => {
		this.props.navigation.goToSSOLogin();
	};

	private validateInputs = (event: React.ChangeEvent<HTMLInputElement>) => {
		const {
			target: {value},
		} = event;
		this.setState({inputString: value});
	};

	public override render() {
		const message = this.state.message || this.props.appState.lastError || "";
		const {isLoggingIn} = this.props.appState;
		const {user} = this.props.appState;
		const {inputString} = this.state;
		const {isOrganizationSwitchView = false} = this.props;

		return (
			<LoginWindowStyled className="LoginWindow">
				<FormContainerStyled
					className="formContainer hbox alignCenter login"
					$loginWithSSO={false}
					$isOrganizationSwitchView={isOrganizationSwitchView}
				>
					{isOrganizationSwitchView && (
						<div className="closeButtonContainer">
							<IconButton
								className="close"
								icon="close"
								onClick={this.props.handleClose}
							/>
						</div>
					)}
					<SVGIcon
						classNames="xyicon-logo"
						icon="xyiconlogo"
					/>
					<form
						className="vbox"
						role="form"
						name="formLogin"
						noValidate
						autoCapitalize="off"
						onSubmit={this.onSubmit}
					>
						<div className="inputGroup login">
							<div className="labelName">Username</div>
							<input
								id="userName"
								name="userName"
								type="text"
								className="Input"
								placeholder="Username"
								onChange={(event) => this.validateInputs(event)}
								value={isOrganizationSwitchView ? user?.email : inputString}
								disabled={isOrganizationSwitchView}
							/>
						</div>
						<div className="inputGroup login">
							<div className="labelName">Password</div>
							<input
								id="pwInput"
								name="pwInput"
								type="password"
								className="Input"
								placeholder="************"
								autoComplete="off"
							/>
							{!isOrganizationSwitchView && (
								<div
									className="forgotPassword labelName"
									onClick={this.onForgottenPasswordClick}
								>
									Forgot Password?
								</div>
							)}
						</div>
						<button
							name="submitButton"
							className={ReactUtils.cls("Button primary", {disabled: isLoggingIn})}
							type="submit"
							disabled={isLoggingIn}
						>
							{isLoggingIn ? (
								<div className="loading">
									<LoaderIconV5 />
								</div>
							) : (
								"Log in"
							)}
						</button>
						{!isOrganizationSwitchView && (
							<div className="sso-button-container">
								<button
									type="button"
									className="sso-login-text"
									disabled={isLoggingIn}
									onClick={this.goToLoginWithSSO}
								>
									or log in with SSO
								</button>
							</div>
						)}
						<div className="labelName help">
							<a
								href="https://support.xyicon.com/docs"
								target="_blank"
							>
								<CircleQuestionIcon />
								<p>Help</p>
							</a>
						</div>
						<div className="errorMessage">{message}</div>
					</form>
				</FormContainerStyled>
			</LoginWindowStyled>
		);
	}

	private handleLoginStateChange = (state: XyiconLoginAndSSOLoginState) => {
		this.setState(state);
	};

	private onSubmit = async (event: React.ChangeEvent<HTMLFormElement>) => {
		event.preventDefault();
		const {transport, appState, isOrganizationSwitchView, navigation, handleOrganizationSwitch} = this.props;
		this.props.appState.lastError = "";
		this.props.appState.isLoggingIn = true;

		await handleLogin({
			event,
			transport,
			appState,
			navigation,
			isOrganizationSwitchView,
			loginWithSSO: false,
			handleOrganizationSwitch,
			handleLoginStateChange: this.handleLoginStateChange,
		});

		this.props.appState.isLoggingIn = false;
	};
}
